import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuoteModal({ isOpen, onClose }: QuoteModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d6srspqd1d31f3q965fg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-blue-dark/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-green-dark to-brand-blue px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-heading font-bold text-xl">{t('qm_title')}</h2>
              <p className="text-white/80 text-sm mt-0.5">{t('qm_sub')}</p>
              <div className="flex items-center gap-4 mt-2.5">
                <div className="flex items-center gap-1.5 text-white/90 text-sm font-semibold">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-building-2-line text-brand-green-light text-base" />
                  </div>
                  Professional Home Multiservices
                </div>
                <a
                  href="tel:7542494067"
                  className="flex items-center gap-1.5 text-white/90 text-sm font-semibold hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-brand-green-light text-base" />
                  </div>
                  (754) 249-4067
                </a>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-green-light mx-auto mb-4">
                <i className="ri-check-line text-3xl text-brand-green-dark" />
              </div>
              <h3 className="font-heading font-bold text-brand-blue-dark text-xl mb-2">{t('qm_success_t')}</h3>
              <p className="text-gray-500 text-sm mb-6">{t('qm_success_d')}</p>
              <button onClick={onClose} className="bg-brand-green hover:bg-brand-green-dark text-white font-semibold px-8 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                {t('qm_close')}
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('qm_name')} *</label>
                  <input name="nombre" required type="text" placeholder={t('qm_name_ph')} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('qm_phone')} *</label>
                  <input name="telefono" required type="tel" placeholder={t('qm_phone_ph')} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('qm_email_label')} *</label>
                <input name="email" required type="email" placeholder={t('qm_email_ph')} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('qm_service')} *</label>
                <select name="servicio" required className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green cursor-pointer">
                  <option value="">{t('qm_service_ph')}</option>
                  <option value="Limpieza Post-Mudanza">{t('qm_svc1')}</option>
                  <option value="Limpieza de Ventanas">{t('qm_svc2')}</option>
                  <option value="Lavado a Presión">{t('qm_svc3')}</option>
                  <option value="Valet Trash Pick-Up">{t('qm_svc4')}</option>
                  <option value="Múltiples Servicios">{t('qm_svc5')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('qm_address')}</label>
                <input name="direccion" type="text" placeholder={t('qm_address_ph')} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('qm_details')}</label>
                <textarea name="detalles" maxLength={500} rows={3} placeholder={t('qm_details_ph')} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green resize-none" />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-brand-green-dark to-brand-blue text-white font-semibold py-3 rounded-full hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {isLoading ? t('qm_loading') : t('qm_btn')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
